import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode, checkActionCode, sendEmailVerification } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import styles from './EmailVerificationPage.module.css';

function EmailVerificationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'already-verified'
  const [errorMessage, setErrorMessage] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      const oobCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');

      // Si pas de code, vérifier si l'utilisateur est connecté et a déjà vérifié son email
      if (!oobCode) {
        if (currentUser) {
          if (currentUser.emailVerified) {
            setStatus('already-verified');
          } else {
            setStatus('error');
            setErrorMessage('Lien de vérification manquant. Merci de cliquer sur le lien dans ton email.');
          }
        } else {
          setStatus('error');
          setErrorMessage('Merci de cliquer sur le lien de vérification dans ton email.');
        }
        return;
      }

      // Vérifier que c'est bien une action de vérification d'email
      if (mode !== 'verifyEmail') {
        setStatus('error');
        setErrorMessage('Lien invalide.');
        return;
      }

      try {
        // Vérifier que le code est valide
        await checkActionCode(auth, oobCode);
        
        // Appliquer le code pour vérifier l'email
        await applyActionCode(auth, oobCode);
        
        setStatus('success');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error verifying email:', error);
        
        if (error.code === 'auth/expired-action-code') {
          setErrorMessage('Ce lien de vérification a expiré. Merci de demander un nouveau lien.');
        } else if (error.code === 'auth/invalid-action-code') {
          setErrorMessage('Ce lien de vérification est invalide ou a déjà été utilisé.');
        } else if (error.code === 'auth/user-disabled') {
          setErrorMessage('Ce compte a été désactivé.');
        } else {
          setErrorMessage('Une erreur est survenue lors de la vérification. Merci de réessayer.');
        }
        setStatus('error');
      }
    };

    verifyEmail();
  }, [searchParams, currentUser]);

  const handleResendVerification = async () => {
    if (!currentUser) {
      setErrorMessage('Merci de te connecter pour renvoyer l\'email de vérification.');
      return;
    }

    setResending(true);
    setErrorMessage('');

    try {
      await sendEmailVerification(currentUser);
      setStatus('success');
      setErrorMessage('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error resending verification email:', error);
      setErrorMessage('Erreur lors de l\'envoi de l\'email. Merci de réessayer.');
    } finally {
      setResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/');
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {status === 'verifying' && (
          <div className={styles.card}>
            <div className={styles.loadingSpinner}></div>
            <h1 className={styles.title}>Vérification en cours...</h1>
            <p className={styles.subtitle}>Merci de patienter pendant que nous vérifions ton email.</p>
          </div>
        )}

        {status === 'success' && (
          <div className={styles.card}>
            <div className={styles.successIcon}>✓</div>
            <h1 className={styles.title}>Email vérifié avec succès ! 🎉</h1>
            <p className={styles.subtitle}>
              Ton adresse email a été vérifiée. Tu peux maintenant te connecter et accéder à toutes les fonctionnalités.
            </p>
            <button
              type="button"
              className={styles.ctaButton}
              onClick={handleGoToLogin}
            >
              Se connecter
            </button>
          </div>
        )}

        {status === 'already-verified' && (
          <div className={styles.card}>
            <div className={styles.successIcon}>✓</div>
            <h1 className={styles.title}>Email déjà vérifié</h1>
            <p className={styles.subtitle}>
              Ton adresse email est déjà vérifiée. Tu peux te connecter normalement.
            </p>
            <button
              type="button"
              className={styles.ctaButton}
              onClick={handleGoToLogin}
            >
              Se connecter
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className={styles.card}>
            <div className={styles.errorIcon}>✕</div>
            <h1 className={styles.title}>Erreur de vérification</h1>
            <p className={styles.subtitle}>{errorMessage}</p>
            {currentUser && !currentUser.emailVerified && (
              <button
                type="button"
                className={styles.resendButton}
                onClick={handleResendVerification}
                disabled={resending}
              >
                {resending ? 'Envoi en cours...' : 'Renvoyer l\'email de vérification'}
              </button>
            )}
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handleGoToLogin}
            >
              Retour à l'accueil
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmailVerificationPage;

